import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react'
import './WhackAMole.scss'
import { gsap } from 'gsap'
import Modal from './common/Modal'
import styles from './WhackAMole.module.css'

// eslint-disable-next-line no-unused-vars
// const C = CSSPlugin;
const MOLE_LENGTH = 9
const MOLE_HEIGHT = 130
const MOLE_SPEED = 0.3
const APPEARANCE_INTERVAL = 1000
const MOLE = {
  default: {
    eyes: ['●', '●'],
    complexion: 'sienna',
    height: MOLE_HEIGHT,
  },
  hurt: {
    eyes: ['>', '<'],
    complexion: '#f7504a',
    height: MOLE_HEIGHT,
  },
}
const TIME_LIMIT = {
  min: 0,
  sec: 20,
}

const createArray = (length, elem) => {
  return new Array(length).fill(elem)
}

const useCountDownTimer = () => {
  const [timeLimit, setTimeLimit] = useState({ min: 0, sec: 0 })
  const intervalID = useRef(null)

  const formatTime = useCallback((num) => {
    let result = num

    if (num < 10) {
      result = `0${num}`
    }
    return result
  }, [])

  const initializeTime = useCallback((timeLimit) => {
    const newTimeLimit = timeLimit
    if (timeLimit.min >= 60) {
      newTimeLimit.min = 0
    }

    if (timeLimit.sec >= 60) {
      newTimeLimit.sec = 0
    }

    setTimeLimit(newTimeLimit)
  }, [])

  const stopTime = useCallback(() => {
    clearInterval(intervalID.current)
  }, [])

  const startTime = useCallback(() => {
    const tick = () => {
      setTimeLimit((prevTimeLimit) => {
        const newTimeLimit = Object.assign({}, prevTimeLimit)

        if (newTimeLimit.min > 0 && newTimeLimit.sec <= 0) {
          newTimeLimit.min = newTimeLimit.min - 1
        }

        if (newTimeLimit.min >= 0 && newTimeLimit.sec <= 0) {
          newTimeLimit.sec = 45
        } else {
          newTimeLimit.sec = (newTimeLimit.sec % 60) - 1
        }

        if (newTimeLimit.min <= 0 && newTimeLimit.sec <= 0) {
          stopTime()
        }

        return newTimeLimit
      })
    }

    intervalID.current = setInterval(tick, 1000)
  }, [stopTime])

  return [timeLimit, initializeTime, startTime, formatTime]
}

const useMoleAnimation = () => {
  const moleElementRef = useRef(null)

  let moveMoleTimeline = null

  const sinkMole = useCallback(() => {
    if (moveMoleTimeline) {
      moveMoleTimeline.kill()
    }

    gsap.to(moleElementRef.current, { duration: 0.1, y: 0 })
  }, [])

  const moveMole = useCallback(() => {
    moveMoleTimeline = gsap.timeline({ repeat: 1, yoyo: true })
    moveMoleTimeline.to(moleElementRef.current, {
      duration: 0.5,
      y: -MOLE_HEIGHT,
    })
  }, [])

  return [moleElementRef, sinkMole, moveMole]
}

const useElementScale = () => {
  const [scale, setScale] = useState(1)
  const elementRef = useRef(null)

  const updateScale = useCallback(() => {
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight
    const element = elementRef.current
    const width = element ? element.clientWidth : 0
    const height = element ? element.clientHeight : 0
    const ratio = height / width
    const screenRatio = screenHeight / screenWidth

    if (screenRatio > ratio) {
      setScale(screenWidth / width)
    } else {
      setScale(screenHeight / height)
    }
  }, [])

  useLayoutEffect(() => {
    updateScale()
    window.addEventListener('resize', () => updateScale())
  })
  return [scale, elementRef]
}

const useMoles = (stage) => {
  const [moles, setMoles] = useState(createArray(MOLE_LENGTH, MOLE.default))

  const moveMole = useCallback((num) => {
    setMoles((prevMoles) =>
      prevMoles.map((prevMole) =>
        prevMole.num === num
          ? {
              ...prevMole,
              isMove: true,
            }
          : prevMole
      )
    )
  }, [])

  const initializeMoles = useCallback(() => {
    setMoles((prevMoles) =>
      prevMoles.map((prevMole, i) => ({
        ...MOLE.default,
        isMove: false,
        isStruck: false,
        num: i + 1,
      }))
    )
  }, [])

  const hitMole = useCallback((num) => {
    setMoles((prevMoles) => {
      const newMoles = prevMoles.map((prevMole) =>
        prevMole.num === num
          ? {
              ...prevMole,
              ...MOLE.hurt,
              isStruck: true,
            }
          : prevMole
      )

      return newMoles
    })
  }, [])

  return [moles, moveMole, initializeMoles, hitMole]
}

const ButtonComponent = ({ className, ...props }) => (
  <button className={`${styles.Button} ${className}`} {...props}></button>
)

const Button = (props) => <ButtonComponent {...props} />

const DialogComponent = ({ className, title, text, buttonName, callback }) => (
  <div className={className}>
    <div className={styles['dialog-content']}>
      <div className={styles['dialog-content-inner']}>
        <h1 className={styles['dialog-title']}>{title}</h1>
        <p className={styles['dialog-text']}>{text}</p>
        <Button className='dialog-button' onClick={callback}>
          {buttonName}
        </Button>
      </div>
    </div>
    <div className={styles['dialog-overlay']}></div>
  </div>
)

const Dialog = (props) => <DialogComponent {...props} />

const DisplayComponent = ({ className, title, text }) => (
  <div className={`${styles.display} ${className}`}>{`${title}: ${text}`}</div>
)

const Display = (props) => <DisplayComponent {...props} />

const MoleComponent = ({ className, mole, style, ...props }) => {
  const [moleElementRef, sinkMole, moveMole] = useMoleAnimation()

  useEffect(() => {
    if (mole.isMove) {
      moveMole()
    }

    if (mole.isStruck) {
      sinkMole()
    }
  }, [mole.isMove, mole.isStruck, moveMole, sinkMole])

  return (
    <div
      className={`${className} ${styles.mole} ${className}-${mole.num}`}
      ref={moleElementRef}
      style={{
        ...style,
        height: `${mole.height}px`,
        bottom: `-${mole.height}px`,
        backgroundColor: `${mole.complexion}`,
      }}
      data-id={mole.num}
      {...props}
    >
      <div className={styles['mole-eyes']}>
        {mole.eyes.map((eye, index) => (
          <div className={styles['mole-eye']} key={index}>
            {eye}
          </div>
        ))}
      </div>
      <div className={styles['mole-mouth']}>
        <div className={styles['mole-nose']}></div>
        <div
          className={`${styles['mole-whiskers']} ${styles['mole-whiskers-left']}`}
        >
          <span className={styles['mole-whiskers-whisker']}></span>
        </div>
        <div
          className={`${styles['mole-whiskers']} ${styles['mole-whiskers-right']}`}
        >
          <span className={styles['mole-whiskers-whisker']}></span>
        </div>
      </div>
    </div>
  )
}

const Mole = (props) => <MoleComponent {...props} />

const TextEmergeEffectComponent = ({ className, text }) => {
  console.log('TextEmergeEffectComponent is rendered')
  return (
    <p
      className={`${styles.textMerge} ${className}`}
      style={{
        fontSize: '1.5rem',
      }}
    >
      {text}
    </p>
  )
}

const TextEmergeEffect = (props) => <TextEmergeEffectComponent {...props} />

const WhackAMoleComponent = ({
  className,
  feature,
  onDone,
  start = null,
  end = null,
}) => {
  const [isStart, setIsStart] = useState(false)
  const [isStop, setIsStop] = useState(false)
  const [hitCount, setHitCount] = useState(0)
  const [modal, setModal] = useState(true),
    [modalType, setModalType] = useState('START'),
    [playAgain, setPlayAgain] = useState(false),
    reset = () => {
      setPlayAgain(true)
      setHitCount(0)
      initializeGame()
    }

  const [scale, elementRef] = useElementScale()
  const [moles, moveMole, initializeMoles, hitMole] = useMoles()
  const [timeLimit, initializeTime, startTime, formatTime] = useCountDownTimer()

  const intervalID = useRef(null)

  const moveMoles = useCallback(() => {
    intervalID.current = setInterval(() => {
      initializeMoles()

      const randomMoleNum = Math.floor(Math.random() * MOLE_LENGTH) + 1
      moveMole(randomMoleNum)
    }, APPEARANCE_INTERVAL)
  }, [initializeMoles, moveMole])

  const stopMoles = useCallback(() => {
    clearInterval(intervalID.current)
  }, [])

  const startGame = useCallback(() => {
    setIsStart(true)
    startTime()
    moveMoles()
  }, [])

  const stopGame = useCallback(() => {
    setIsStop(true)
    stopMoles()
  }, [])

  const initializeGame = useCallback(() => {
    setIsStart(false)
    setIsStop(false)
    setHitCount(0)
    initializeMoles()
    initializeTime(TIME_LIMIT)
  }, [])

  const handleHit = useCallback((e) => {
    const moleElement = e.target

    const num = parseInt(moleElement.dataset.id, 10)

    hitMole(num)
    setHitCount((prevHitCount) => prevHitCount + 1)
  }, [])

  useEffect(() => {
    initializeGame()
    setModal(true)
    setModalType('START')
    setPlayAgain(false)
  }, [])

  useEffect(() => {
    if (isStart && timeLimit.min <= 0 && timeLimit.sec <= 0) {
      setModal(true)
      setModalType('END')
      if (end) end({ score: hitCount })
      stopGame()
    }
  }, [timeLimit.min, timeLimit.sec])

  return (
    <div className='whack-a-mole' ref={elementRef}>
      <Modal
        feature={feature}
        show={modal}
        type={modalType}
        kind={'WHACK_A_MOLE'}
        intro={'Time to channelize this anger by letting it out on the moles!'}
        stats={{ score: `${hitCount}` }}
        done={() => {
          if (modalType === 'START') {
            if (start) start(playAgain ? 'PLAY_AGAIN' : 'START')
            setModal(false)
            setModalType('')
            startGame()
          } else if (modalType === 'END') {
            setModal(false)
            setModalType('')
            onDone()
          }
        }}
        reset={() => {
          setModal(true)
          setModalType('START')
          reset()
        }}
      />
      {/* {isStop && (
        <Dialog
          className="finish-dialog"
          title="Time's up"
          text={`Your score is ${hitCount}!`}
          buttonName="finish"
          callback={initializeGame}
        />
      )}
      {!isStart && (
        <Dialog
          className="start-dialog"
          title="Whack a Mole"
          text="Time to channelize this anger by letting it out on the moles!"
          buttonName="Let's Go"
          callback={startGame}
        />
      )} */}
      <div className='game-status'>
        <Display
          className='time-limit'
          title='Time limit'
          text={`${formatTime(timeLimit.min)}:${formatTime(timeLimit.sec)}`}
        />
        <Display className='score' title='Score' text={hitCount} />
      </div>
      <div className='stage'>
        {moles &&
          moles.map((mole, i) => (
            <div className='cell' key={i}>
              <div className='hole-mask'>
                <div className='hole'>
                  {mole.isStruck && (
                    // <TextEmergeEffect className='hit-effect' text='hit!' />
                    <TextEmergeEffect className='hit-effect' text='hit!' />
                  )}
                  <Mole className='mole' mole={mole} onClick={handleHit} />
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}

export default WhackAMoleComponent
